// ── InfoIcon + definition modal ────────────────────────────────────────────
// The explanation-layer UI primitive. Put infoIcon('<metricKey>') next to any
// metric label; clicking it opens a modal with what / why / how from the
// metric registry. One global delegated listener handles every icon, so pages
// only emit the markup — no per-page wiring.
//
//   import { infoIcon } from '../shared/components/InfoIcon';
//   `<span class="metric-label">MPI ${infoIcon('mpi')}</span>`
//
// Call initInfoLayer() ONCE at app boot (App.render) to mount the modal +
// listener. Missing keys degrade gracefully ("definition coming soon").

import { getMetricDef, MetricDef } from '../lib/metricDefinitions';

/** Inline info icon. Renders an unobtrusive ⓘ that opens the definition modal. */
export function infoIcon(key: string): string {
  return `<span class="info-icon" data-metric="${key}" role="button" tabindex="0" aria-label="What is this?" title="What is this?">ⓘ</span>`;
}

let _mounted = false;

/** Mount the modal + the single delegated click handler. Idempotent. */
export function initInfoLayer(): void {
  if (_mounted) return;
  _mounted = true;

  // inject styles once
  if (!document.getElementById('info-layer-styles')) {
    const style = document.createElement('style');
    style.id = 'info-layer-styles';
    style.textContent = `
      .info-icon{display:inline-flex;align-items:center;justify-content:center;width:15px;height:15px;
        margin-left:4px;border-radius:50%;font-size:10px;font-weight:700;line-height:1;cursor:pointer;
        color:#3b82f6;background:#eaf2ff;border:1px solid #bfd7fe;vertical-align:middle;user-select:none;
        transition:background .12s,color .12s;}
      .info-icon:hover{background:#3b82f6;color:#fff;}
      #info-modal-overlay{position:fixed;inset:0;background:rgba(15,23,42,.5);z-index:9500;
        display:none;align-items:flex-start;justify-content:center;padding-top:96px;}
      #info-modal-card{background:#fff;border-radius:16px;box-shadow:0 24px 64px rgba(0,0,0,.25);
        width:100%;max-width:460px;overflow:hidden;animation:infoPop .12s ease;}
      @keyframes infoPop{from{transform:translateY(6px);opacity:.6}to{transform:none;opacity:1}}
      #info-modal-card .imc-head{display:flex;justify-content:space-between;align-items:flex-start;
        gap:12px;padding:16px 18px;border-bottom:1px solid #eef2f7;}
      #info-modal-card .imc-term{font-size:16px;font-weight:700;color:#1e293b;}
      #info-modal-card .imc-cat{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;
        color:#3b82f6;background:#eaf2ff;border-radius:6px;padding:2px 8px;display:inline-block;margin-top:4px;}
      #info-modal-card .imc-x{border:none;background:none;font-size:18px;color:#94a3b8;cursor:pointer;line-height:1;}
      #info-modal-card .imc-body{padding:16px 18px;display:flex;flex-direction:column;gap:14px;}
      #info-modal-card .imc-row .imc-k{font-size:11px;font-weight:700;text-transform:uppercase;
        letter-spacing:.04em;color:#94a3b8;margin-bottom:3px;}
      #info-modal-card .imc-row .imc-v{font-size:14px;color:#334155;line-height:1.5;}
      #info-modal-card .imc-related{display:flex;flex-wrap:wrap;gap:6px;}
      #info-modal-card .imc-related span{font-size:11px;color:#3b82f6;background:#eaf2ff;border:1px solid #bfd7fe;
        border-radius:6px;padding:2px 8px;cursor:pointer;}
    `;
    document.head.appendChild(style);
  }

  // mount modal shell
  if (!document.getElementById('info-modal-overlay')) {
    const overlay = document.createElement('div');
    overlay.id = 'info-modal-overlay';
    overlay.innerHTML = `<div id="info-modal-card"></div>`;
    document.body.appendChild(overlay);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeInfo(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeInfo(); });
  }

  // one delegated handler for every current + future .info-icon
  document.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement)?.closest('.info-icon') as HTMLElement | null;
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    openInfo(el.dataset.metric || '');
  });
}

export function openInfo(key: string): void {
  const overlay = document.getElementById('info-modal-overlay');
  const card = document.getElementById('info-modal-card');
  if (!overlay || !card) return;
  const def = getMetricDef(key);
  card.innerHTML = def ? renderDef(def) : renderMissing(key);
  overlay.style.display = 'flex';
  card.querySelector('.imc-x')?.addEventListener('click', closeInfo);
  card.querySelectorAll<HTMLElement>('.imc-related span').forEach(s =>
    s.addEventListener('click', () => openInfo(s.dataset.metric || '')));
}

export function closeInfo(): void {
  const overlay = document.getElementById('info-modal-overlay');
  if (overlay) overlay.style.display = 'none';
}

function renderDef(d: MetricDef): string {
  const related = (d.related || []).map(r => getMetricDef(r)).filter(Boolean) as MetricDef[];
  return `
    <div class="imc-head">
      <div><div class="imc-term">${d.term}</div><span class="imc-cat">${d.category}</span></div>
      <button class="imc-x" aria-label="Close">✕</button>
    </div>
    <div class="imc-body">
      <div class="imc-row"><div class="imc-k">What it is</div><div class="imc-v">${d.what}</div></div>
      <div class="imc-row"><div class="imc-k">Why it matters</div><div class="imc-v">${d.why}</div></div>
      <div class="imc-row"><div class="imc-k">How it's computed</div><div class="imc-v">${d.how}</div></div>
      ${related.length ? `<div class="imc-row"><div class="imc-k">Related</div>
        <div class="imc-related">${related.map(r => `<span data-metric="${r.key}">${r.term}</span>`).join('')}</div></div>` : ''}
    </div>`;
}

function renderMissing(key: string): string {
  return `
    <div class="imc-head"><div class="imc-term">${key || 'Metric'}</div>
      <button class="imc-x" aria-label="Close">✕</button></div>
    <div class="imc-body"><div class="imc-row"><div class="imc-v">Definition coming soon.</div></div></div>`;
}
